import { useEffect, useMemo, useRef, useState } from 'react';
import { C } from '../../lib/tokens.js';
import { t } from '../../lib/data.js';
import { dateShort, dateLong, timeShort } from '../../lib/utils.js';
import { Spinner } from '../../components/ui/Misc.jsx';
import { SearchableSelect } from '../../components/ui/Fields.jsx';

const MAX_STICK_HEIGHT = 280;
const CAP_SIZE = 16;
const CAP_WIDTH = 26;   // shared with BranchStrip so a single-update cap and a combined stick's branches are the same rectangle
const TOP_GAP = 40;      // breathing room between the tallest cap and the chart's top edge
const STICK_AREA_HEIGHT = MAX_STICK_HEIGHT + CAP_SIZE + TOP_GAP;
const MS_PER_DAY = 86400000;
const PX_PER_DAY = 6;    // chronological scale - real gaps in time become real gaps on screen
const MIN_GAP = 90;      // enforced minimum between different days
const PADDING = 44;
const LABEL_HEIGHT = 26; // just the date under each stick - version/asset show on hover

const UPDATE_TYPE_STYLES = {
  MJ: { label: 'Major',    color: C.rust,   stickHeight: 280 },
  MI: { label: 'Minor',    color: C.brass,  stickHeight: 180 },
  RV: { label: 'Revision', color: C.liquid, stickHeight: 110 },
  PA: { label: 'Patch',    color: C.patina, stickHeight: 60 },
};
const UNKNOWN_TYPE_STYLE = { label: 'Unknown', color: C.silver, stickHeight: 32 };

function typeStyle(updateType, updateTypeLabel) {
  const s = UPDATE_TYPE_STYLES[updateType] || UNKNOWN_TYPE_STYLE;
  return updateTypeLabel ? { ...s, label: updateTypeLabel } : s;
}

function effectiveDate(entry) {
  return entry.appliedDate || entry.plannedDate;
}

// "to" needs the END of that calendar day, not midnight, so entries on that day are included.
function inDateRange(entry, from, to) {
  const t = new Date(effectiveDate(entry)).getTime();
  if (from && t < new Date(from).getTime()) return false;
  if (to && t >= new Date(to).getTime() + MS_PER_DAY) return false;
  return true;
}

function startOfDay(time) {
  const d = new Date(time);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// Interval adapts to span so a long history doesn't print hundreds of labels.
function generateAxisTicks(minTime, maxTime) {
  const daySpan = (maxTime - minTime) / MS_PER_DAY;
  const ticks = [];
  if (daySpan <= 0) return [minTime];

  if (daySpan <= 21) {
    for (let t = startOfDay(minTime); t <= maxTime; t += MS_PER_DAY) ticks.push(t);
  } else if (daySpan <= 90) {
    for (let t = startOfDay(minTime); t <= maxTime; t += 7 * MS_PER_DAY) ticks.push(t);
  } else if (daySpan <= 400) {
    const d = new Date(minTime);
    d.setDate(1); d.setHours(0, 0, 0, 0);
    while (d.getTime() <= maxTime) { ticks.push(d.getTime()); d.setMonth(d.getMonth() + 1); }
  } else {
    const d = new Date(minTime);
    d.setDate(1); d.setHours(0, 0, 0, 0);
    d.setMonth(Math.floor(d.getMonth() / 3) * 3);
    while (d.getTime() <= maxTime) { ticks.push(d.getTime()); d.setMonth(d.getMonth() + 3); }
  }

  // Keep the real last date visible even if it's off-grid.
  if (ticks.length === 0 || ticks[ticks.length - 1] < maxTime - MS_PER_DAY) ticks.push(maxTime);
  return ticks;
}

// Coarser units past ~2 months so a multi-year history doesn't print "420d".
function formatGap(days) {
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.round(days / 7)}w`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${Math.round(days / 365)}y`;
}

// Detail views need the time too, not just the day dateShort/dateLong give.
function dateTimeLong(date) {
  return `${dateLong(date)} · ${timeShort(date)}`;
}

function stateLabel(state) {
  if (state === 'planned') return t('assets.planned');
  if (state === 'cancelled') return t('assets.cancelled');
  if (state === 'rolledback') return t('assets.rolled_back');
  return t('assets.applied');
}

// Spells out what the tooltip shows visually, for screen readers / no-hover.
function entryAriaLabel(entry, style, state, date) {
  const parts = [
    entry.assetName || 'Unknown asset',
    `version ${entry.version || '?'}`,
    style.label,
    stateLabel(state),
    dateTimeLong(date),
  ];
  if (entry.statusLabel) parts.push(entry.statusLabel);
  if (entry.description) parts.push(entry.description);
  return parts.join(', ');
}

function groupAriaLabel(group, date) {
  const versions = group.map(e => e.version || '?').join(', ');
  return `${group[0].assetName || 'Unknown asset'}, ${group.length} updates on ${dateTimeLong(date)}: ${versions}`;
}

// legendFilter is {kind:'type'|'status', value} or null (no filter).
function matchesLegendFilter(entry, legendFilter) {
  if (!legendFilter) return true;
  if (legendFilter.kind === 'type') return entry.updateType === legendFilter.value;
  return visualState(entry) === legendFilter.value;
}

function groupMatchesLegendFilter(group, legendFilter) {
  return !legendFilter || group.some(e => matchesLegendFilter(e, legendFilter));
}

function activateOnKey(e, callback) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault();
    e.stopPropagation();
    callback();
  }
}

function dayKeyOf(entry) {
  return new Date(effectiveDate(entry)).toDateString();
}

// Bundles same-calendar-day groups so their sticks can share one date label.
function clusterByDay(groups) {
  const clusters = [];
  groups.forEach((group, gi) => {
    const day = dayKeyOf(group[0]);
    const last = clusters[clusters.length - 1];
    if (last && last.day === day) {
      last.groupIndices.push(gi);
    } else {
      clusters.push({ day, groupIndices: [gi] });
    }
  });
  return clusters;
}

// Cancelled/rolled-back take priority; otherwise falls back to the date check
// rather than trusting a status string that isn't always reliably populated.
function visualState(entry) {
  if (entry.status === 'CL') return 'cancelled';
  if (entry.status === 'RB') return 'rolledback';
  if (!entry.appliedDate && entry.plannedDate) return 'planned';
  return 'applied';
}

// Same asset + same day collapses into one group (one combined stick).
// Bucketed by a Map, not by comparing to the previous entry, since same-key
// entries aren't guaranteed adjacent once other assets interleave by time.
function groupByDay(entries) {
  const groups = [];
  const indexByKey = new Map();
  entries.forEach(e => {
    const dayKey = `${e.assetId || ''}|${new Date(effectiveDate(e)).toDateString()}`;
    let idx = indexByKey.get(dayKey);
    if (idx === undefined) {
      idx = groups.length;
      indexByKey.set(dayKey, idx);
      groups.push([]);
    }
    groups[idx].push(e);
  });
  return groups;
}

// Lays groups out on a real day-scale axis, nudging close ones apart by
// MIN_GAP; stretches to fill available width unless that would cram things
// together, in which case it falls back to a fixed scale and scrolls instead.
function layout(entries, availableWidth) {
  const groups = groupByDay(entries);
  const times = groups.map(g => new Date(effectiveDate(g[0])).getTime());
  const minTime = Math.min(...times);
  const maxTime = Math.max(...times);
  const daySpan = (maxTime - minTime) / MS_PER_DAY;

  const usableWidth = Math.max(0, (availableWidth || 0) - PADDING * 2);
  const naturalSpanWidth = daySpan * PX_PER_DAY;
  const pxPerDay = (availableWidth && daySpan > 0 && naturalSpanWidth < usableWidth)
    ? usableWidth / daySpan
    : PX_PER_DAY;

  let prevX = -Infinity;
  const groupX = times.map(time => {
    const raw = ((time - minTime) / MS_PER_DAY) * pxPerDay;
    const x = Math.max(raw, prevX + MIN_GAP);
    prevX = x;
    return x + PADDING;
  });

  const contentWidth = (groupX[groupX.length - 1] ?? 0) + PADDING;
  const width = Math.max(contentWidth, availableWidth || 0);
  return { groups, groupX, times, width, minTime, maxTime };
}

// Maps a time to x on the SAME collision-adjusted positions the sticks use
// (groupX), not the pure day scale - keeps axis ticks/"today" aligned with
// the sticks even where MIN_GAP has nudged them apart from their true spot.
function interpolateX(t, times, groupX) {
  if (t <= times[0]) return groupX[0];
  const last = times.length - 1;
  if (t >= times[last]) return groupX[last];
  for (let i = 1; i <= last; i++) {
    if (t <= times[i]) {
      const t0 = times[i - 1], t1 = times[i], x0 = groupX[i - 1], x1 = groupX[i];
      const frac = t1 === t0 ? 0 : (t - t0) / (t1 - t0);
      return x0 + frac * (x1 - x0);
    }
  }
  return groupX[last];
}

// Rounded rectangle, not a circle, to match a combined stick's BranchStrip.
function StickCap({ state, color, size = 16 }) {
  const base = {
    width: CAP_WIDTH, height: size, borderRadius: 4, flex: 'none', boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  if (state === 'planned') return <div style={{ ...base, background: C.tin, border: `2px dotted ${color}` }} />;
  if (state === 'cancelled') {
    return (
      <div style={{ ...base, background: C.mercury, border: `2px solid ${C.silver}` }}>
        <span className="material-icons" style={{ fontSize: size - 6, color: C.iron }}>close</span>
      </div>
    );
  }
  if (state === 'rolledback') {
    return (
      <div style={{ ...base, background: C.tin, border: `2px solid ${color}` }}>
        <span className="material-icons" style={{ fontSize: size - 6, color }}>undo</span>
      </div>
    );
  }
  return (
    <div style={{ ...base, background: color }}>
      <span className="material-icons" style={{ fontSize: size - 6, color: C.tin }}>check</span>
    </div>
  );
}

// "fixed", not "absolute" - overflow-x on the scroll container forces
// overflow-y:auto too, which would clip an absolutely-positioned tooltip.
function StickTooltip({ entry, style, state, date, anchor }) {
  const isPlanned = state === 'planned';
  return (
    <div style={{
      position: 'fixed', left: anchor.x, top: anchor.y, transform: 'translate(-50%, -100%)',
      zIndex: 1000, width: 210, background: C.tin, border: `1px solid ${C.mercury}`, borderRadius: 6,
      boxShadow: '0 6px 16px rgba(34,52,105,0.18)', padding: '10px 12px', textAlign: 'left', pointerEvents: 'none',
    }}>
      <div style={{
        position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)',
        width: 10, height: 10, background: C.tin, borderRight: `1px solid ${C.mercury}`, borderBottom: `1px solid ${C.mercury}`,
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span className="material-icons" style={{ fontSize: 14, color: C.deepsea }}>dns</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {entry.assetName || 'Unknown asset'}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
        <span style={{ fontFamily: 'Roboto Mono', fontSize: 12, fontWeight: 600, color: C.deepsea }}>{entry.version || '?'}</span>
        <span style={{
          display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 999,
          background: `${style.color}1F`, color: style.color, fontSize: 10, fontWeight: 600,
          textTransform: 'uppercase', letterSpacing: '0.03em',
        }}>
          {style.label}
        </span>
      </div>

      <div style={{ fontSize: 11, color: C.iron }}>
        {isPlanned ? `${t('assets.planned')} ${dateTimeLong(date)}` : dateTimeLong(date)}
        {entry.statusLabel ? ` · ${entry.statusLabel}` : ''}
      </div>

      {entry.description ? (
        <div style={{ fontSize: 11, color: C.fg, marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.mercury}` }}>
          {entry.description}
        </div>
      ) : null}
    </div>
  );
}

// Hover shows the tooltip; a tap pins it open too, since touch has no hover.
function Stick({ entry, x, showDate, selected, dimmed, onSelect }) {
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const [selectedAnchor, setSelectedAnchor] = useState(null);
  const capRef = useRef(null);
  const style = typeStyle(entry.updateType, entry.updateTypeLabel);
  const state = visualState(entry);
  const isPlanned = state === 'planned';
  const isCancelled = state === 'cancelled';
  const date = effectiveDate(entry);

  function computeAnchor() {
    const rect = capRef.current?.getBoundingClientRect();
    return rect ? { x: rect.left + rect.width / 2, y: rect.top - 8 } : null;
  }

  useEffect(() => {
    setSelectedAnchor(selected ? computeAnchor() : null);
  }, [selected]);

  const anchor = hoverAnchor || selectedAnchor;

  return (
    <div
      data-stick-id={entry.id}
      role="button" tabIndex={0} aria-pressed={selected}
      aria-label={entryAriaLabel(entry, style, state, date)}
      onMouseEnter={() => setHoverAnchor(computeAnchor())}
      onMouseLeave={() => setHoverAnchor(null)}
      onFocus={() => setHoverAnchor(computeAnchor())}
      onBlur={() => setHoverAnchor(null)}
      onClick={(e) => { e.stopPropagation(); onSelect(entry.id); }}
      onKeyDown={(e) => activateOnKey(e, () => onSelect(entry.id))}
      style={{
        position: 'absolute', left: x, bottom: 0, width: 84, marginLeft: -42,
        display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer',
        opacity: dimmed ? 0.25 : 1, transition: 'opacity 150ms',
      }}
    >
      <div style={{ height: STICK_AREA_HEIGHT, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
        <div ref={capRef}>
          <StickCap state={state} color={style.color} />
        </div>
        <div style={{
          width: 0, flex: 'none', height: style.stickHeight,
          borderLeft: `3px ${isPlanned ? 'dotted' : 'solid'} ${style.color}`,
          opacity: isCancelled ? 0.5 : 1,
        }} />
      </div>
      {anchor && <StickTooltip entry={entry} style={style} state={state} date={date} anchor={anchor} />}

      <div style={{ height: LABEL_HEIGHT, paddingTop: 6, textAlign: 'center' }}>
        <div style={{ fontSize: 10, color: isPlanned ? C.iron : C.silver, whiteSpace: 'nowrap' }}>
          {showDate ? dateShort(date) : ''}
        </div>
      </div>
    </div>
  );
}

const BRANCH_THICKNESS = 16; // tall enough to hold the cancelled/rolled-back icon (see StickCap)

// One horizontal strip per entry, crossing the trunk at its own severity
// height - a branch, not a stacked block, so the group reads as one stick.
function BranchStrip({ entry, trunkHeight }) {
  const style = typeStyle(entry.updateType, entry.updateTypeLabel);
  const state = visualState(entry);
  const bottom = Math.min(Math.max(style.stickHeight - BRANCH_THICKNESS / 2, 0), trunkHeight - BRANCH_THICKNESS);
  const base = {
    position: 'absolute', bottom, left: '50%', transform: 'translateX(-50%)',
    width: CAP_WIDTH, height: BRANCH_THICKNESS, borderRadius: 4, boxSizing: 'border-box',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  if (state === 'planned') return <div style={{ ...base, background: C.tin, border: `2px dotted ${style.color}` }} />;
  if (state === 'cancelled') {
    return (
      <div style={{ ...base, background: C.mercury, border: `2px solid ${C.silver}` }}>
        <span className="material-icons" style={{ fontSize: 10, color: C.iron }}>close</span>
      </div>
    );
  }
  if (state === 'rolledback') {
    return (
      <div style={{ ...base, background: C.tin, border: `2px solid ${style.color}` }}>
        <span className="material-icons" style={{ fontSize: 10, color: style.color }}>undo</span>
      </div>
    );
  }
  return (
    <div style={{ ...base, background: style.color }}>
      <span className="material-icons" style={{ fontSize: 10, color: C.tin }}>check</span>
    </div>
  );
}

// Colors the trunk itself, stacked bottom-up by severity height, instead of
// leaving it a flat gray line with colored branches floating on top of it.
function TrunkSegments({ group }) {
  const sorted = [...group].sort((a, b) =>
    typeStyle(a.updateType, a.updateTypeLabel).stickHeight - typeStyle(b.updateType, b.updateTypeLabel).stickHeight);

  let bottom = 0;
  return sorted.map(entry => {
    const style = typeStyle(entry.updateType, entry.updateTypeLabel);
    const state = visualState(entry);
    const height = style.stickHeight - bottom;
    const segment = (
      <div key={entry.id} style={{
        position: 'absolute', left: '50%', bottom, width: 0, height,
        transform: 'translateX(-50%)',
        borderLeft: `3px ${state === 'planned' ? 'dotted' : 'solid'} ${style.color}`,
        opacity: state === 'cancelled' ? 0.5 : 1,
      }} />
    );
    bottom = style.stickHeight;
    return segment;
  });
}

// Count instead of a type/status dot - a group holds several states at once.
function GroupCap({ count, size = 18 }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%', flex: 'none',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: C.deepsea, color: C.tin, fontSize: 10, fontWeight: 700,
    }}>
      {count}
    </div>
  );
}

// Same-asset, same-day entries render as ONE stick. Hover/click mirrors
// Stick exactly - only the tooltip content differs (lists every entry).
function GroupedStick({ group, x, showDate, selected, dimmed, onSelect }) {
  const [hoverAnchor, setHoverAnchor] = useState(null);
  const [selectedAnchor, setSelectedAnchor] = useState(null);
  const capRef = useRef(null);
  const maxHeight = Math.max(...group.map(e => typeStyle(e.updateType, e.updateTypeLabel).stickHeight));
  const date = effectiveDate(group[0]);

  function computeAnchor() {
    const rect = capRef.current?.getBoundingClientRect();
    return rect ? { x: rect.left + rect.width / 2, y: rect.top - 8 } : null;
  }

  useEffect(() => {
    setSelectedAnchor(selected ? computeAnchor() : null);
  }, [selected]);

  const anchor = hoverAnchor || selectedAnchor;

  return (
    <div
      data-stick-id={`grp:${group.map(e => e.id).join(',')}`}
      role="button" tabIndex={0} aria-pressed={selected}
      aria-label={groupAriaLabel(group, date)}
      onMouseEnter={() => setHoverAnchor(computeAnchor())}
      onMouseLeave={() => setHoverAnchor(null)}
      onFocus={() => setHoverAnchor(computeAnchor())}
      onBlur={() => setHoverAnchor(null)}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      onKeyDown={(e) => activateOnKey(e, onSelect)}
      style={{
        position: 'absolute', left: x, bottom: 0, width: 84, marginLeft: -42,
        display: 'flex', flexDirection: 'column', alignItems: 'center', cursor: 'pointer',
        opacity: dimmed ? 0.25 : 1, transition: 'opacity 150ms',
      }}
    >
      <div style={{ height: STICK_AREA_HEIGHT, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end' }}>
        <div ref={capRef}><GroupCap count={group.length} /></div>
        <div style={{ position: 'relative', width: CAP_WIDTH, height: maxHeight, marginTop: 2 }}>
          <TrunkSegments group={group} />
          {group.map(entry => <BranchStrip key={entry.id} entry={entry} trunkHeight={maxHeight} />)}
        </div>
      </div>
      {anchor && <GroupTooltip group={group} anchor={anchor} />}
      <div style={{ height: LABEL_HEIGHT, paddingTop: 6, textAlign: 'center' }}>
        <div style={{ fontSize: 10, color: C.iron, whiteSpace: 'nowrap' }}>
          {showDate ? dateShort(date) : ''}
        </div>
      </div>
    </div>
  );
}

// Same card as StickTooltip, but lists every entry instead of one.
function GroupTooltip({ group, anchor }) {
  return (
    <div style={{
      position: 'fixed', left: anchor.x, top: anchor.y, transform: 'translate(-50%, -100%)',
      zIndex: 1000, width: 240, maxHeight: 280, overflowY: 'auto', background: C.tin,
      border: `1px solid ${C.mercury}`, borderRadius: 6, boxShadow: '0 6px 16px rgba(34,52,105,0.18)',
      padding: '10px 12px', textAlign: 'left', pointerEvents: 'none',
    }}>
      <div style={{
        position: 'absolute', bottom: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)',
        width: 10, height: 10, background: C.tin, borderRight: `1px solid ${C.mercury}`, borderBottom: `1px solid ${C.mercury}`,
      }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <span className="material-icons" style={{ fontSize: 14, color: C.deepsea }}>dns</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.fg, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {group[0].assetName || 'Unknown asset'}
        </span>
      </div>

      {group.map((entry, i) => {
        const style = typeStyle(entry.updateType, entry.updateTypeLabel);
        const state = visualState(entry);
        const date = effectiveDate(entry);
        const isPlanned = state === 'planned';
        return (
          <div key={entry.id} style={{ paddingTop: i === 0 ? 0 : 8, marginTop: i === 0 ? 0 : 8, borderTop: i === 0 ? 'none' : `1px solid ${C.mercury}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontFamily: 'Roboto Mono', fontSize: 12, fontWeight: 600, color: C.deepsea }}>{entry.version || '?'}</span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', padding: '2px 7px', borderRadius: 999,
                background: `${style.color}1F`, color: style.color, fontSize: 10, fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.03em',
              }}>
                {style.label}
              </span>
            </div>
            <div style={{ fontSize: 11, color: C.iron }}>
              {isPlanned ? `${t('assets.planned')} ${dateTimeLong(date)}` : dateTimeLong(date)}
              {entry.statusLabel ? ` · ${entry.statusLabel}` : ''}
            </div>
            {entry.description ? (
              <div style={{ fontSize: 11, color: C.fg, marginTop: 4 }}>{entry.description}</div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

// One shared date + bracket line under a run of same-day sticks (different
// assets, so not mergeable), instead of repeating the date under each one.
function ClusterDateBadge({ left, right, date }) {
  const top = STICK_AREA_HEIGHT + 6;
  const center = (left + right) / 2;
  return (
    <>
      <div style={{ position: 'absolute', left, top: top + 6, width: right - left, borderTop: `1px solid ${C.silver}`, height: 0 }} />
      <div style={{
        position: 'absolute', left: center, top, transform: 'translateX(-50%)',
        fontSize: 10, fontWeight: 600, color: C.iron, whiteSpace: 'nowrap',
        background: C.tin, padding: '0 4px',
      }}>
        {dateShort(date)}
      </div>
    </>
  );
}

// Elapsed time since the previous cluster - only rendered where the gap is
// wide enough to fit it (see caller), so it never collides with a neighbor.
function GapLabel({ x, label }) {
  return (
    <div style={{
      position: 'absolute', left: x, top: STICK_AREA_HEIGHT + 1, transform: 'translate(-50%, -50%)',
      fontSize: 10, color: C.silver, whiteSpace: 'nowrap', background: C.tin, padding: '0 4px',
      pointerEvents: 'none',
    }}>
      {label}
    </div>
  );
}

// A full ruler along the axis instead of just the two endpoint dates.
function DateAxis({ minTime, maxTime, times, groupX }) {
  const ticks = useMemo(() => generateAxisTicks(minTime, maxTime), [minTime, maxTime]);
  return (
    <div style={{ position: 'relative', height: 16, marginBottom: 4 }}>
      {ticks.map((t, i) => {
        const x = interpolateX(t, times, groupX);
        return (
          <div key={i} style={{ position: 'absolute', left: x, top: 0, transform: 'translateX(-50%)', whiteSpace: 'nowrap' }}>
            <span style={{ fontSize: 10, color: C.silver }}>{dateShort(t)}</span>
          </div>
        );
      })}
    </div>
  );
}

// Vertical "now" line, so planned-vs-applied reads at a glance.
function TodayMarker({ x }) {
  return (
    <>
      <div style={{
        position: 'absolute', left: x, top: 0, bottom: LABEL_HEIGHT, width: 0,
        borderLeft: `1.5px dashed ${C.copper}`, pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', left: x, top: -4, transform: 'translate(-50%, -100%)',
        fontSize: 10, fontWeight: 600, color: C.copper, whiteSpace: 'nowrap',
        background: C.tin, padding: '0 4px', pointerEvents: 'none',
      }}>
        Today · {dateShort(Date.now())}
      </div>
    </>
  );
}

// Legend entries double as filter toggles - click dims non-matching sticks.
function LegendSwatch({ color, height, label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'none',
      padding: '2px 4px', borderRadius: 3, cursor: 'pointer',
      boxShadow: active ? `0 0 0 1px ${color}` : 'none',
    }}>
      <div style={{ width: 6, borderRadius: 2, height: Math.max(10, height / 5), background: color, flex: 'none' }} />
      <span style={{ fontSize: 11, color: C.fg }}>{label}</span>
    </button>
  );
}

function LegendStatus({ state, label, active, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'none',
      padding: '2px 4px', borderRadius: 3, cursor: 'pointer',
      boxShadow: active ? `0 0 0 1px ${C.iron}` : 'none',
    }}>
      <StickCap state={state} color={C.iron} size={14} />
      <span style={{ fontSize: 11, color: C.fg }}>{label}</span>
    </button>
  );
}

function Legend({ legendFilter, onToggle }) {
  const heading = { fontSize: 10, fontWeight: 600, color: C.silver, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 };
  return (
    <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 18, padding: '12px 14px', background: C.platinum, borderRadius: 6, border: `1px solid ${C.mercury}` }}>
      <div>
        <div style={heading}>Update size (line length &amp; color)</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {Object.entries(UPDATE_TYPE_STYLES).map(([code, s]) => (
            <LegendSwatch key={code} color={s.color} height={s.stickHeight} label={s.label}
              active={legendFilter?.kind === 'type' && legendFilter.value === code}
              onClick={() => onToggle({ kind: 'type', value: code })} />
          ))}
        </div>
      </div>
      <div style={{ width: 1, background: C.mercury }} />
      <div>
        <div style={heading}>Status (dot style)</div>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          {[
            ['applied', t('assets.applied')],
            ['planned', t('assets.planned')],
            ['cancelled', t('assets.cancelled')],
            ['rolledback', t('assets.rolled_back')],
          ].map(([state, label]) => (
            <LegendStatus key={state} state={state} label={label}
              active={legendFilter?.kind === 'status' && legendFilter.value === state}
              onClick={() => onToggle({ kind: 'status', value: state })} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function SystemTimeline({ entries, loading }) {
  const [assetFilter, setAssetFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [availableWidth, setAvailableWidth] = useState(0);
  const scrollRef = useRef(null);

  // Measure the scroll container so layout() knows how much room to stretch into.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const observer = new ResizeObserver(entries => {
      const w = entries[0]?.contentRect?.width;
      if (w) setAvailableWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Only one stick selected at a time; clicking outside any stick clears it.
  const [selectedId, setSelectedId] = useState(null);

  // Selection and legend filter are mutually exclusive - each clears the other.
  const [legendFilter, setLegendFilter] = useState(null);

  function selectStick(id) {
    setLegendFilter(null);
    setSelectedId(prev => (prev === id ? null : id));
  }

  function toggleLegendFilter(filter) {
    setSelectedId(null);
    setLegendFilter(prev => (prev && prev.kind === filter.kind && prev.value === filter.value ? null : filter));
  }

  useEffect(() => {
    function handleOutside(e) {
      if (!e.target.closest?.('[data-stick-id]')) setSelectedId(null);
    }
    // Escape is the keyboard equivalent of "click outside".
    function handleEscape(e) {
      if (e.key === 'Escape') { setSelectedId(null); setLegendFilter(null); }
    }
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const assetOptions = useMemo(() => {
    const seen = new Map();
    for (const e of entries || []) {
      if (e.assetId && !seen.has(e.assetId)) seen.set(e.assetId, e.assetName || e.assetId);
    }
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [entries]);

  const filtered = (entries || [])
    .filter(e => !assetFilter || e.assetId === assetFilter)
    .filter(e => inDateRange(e, dateFrom, dateTo));
  const { groups, groupX, times, width, minTime, maxTime } = useMemo(
    () => layout(filtered.length ? filtered : [], availableWidth),
    [filtered, availableWidth]
  );

  const clusters = useMemo(() => clusterByDay(groups), [groups]);

  // One label per gap between consecutive clusters, only where it fits.
  const gapLabels = useMemo(() => {
    const labels = [];
    for (let ci = 1; ci < clusters.length; ci++) {
      const prevXs = clusters[ci - 1].groupIndices.map(gi => groupX[gi]);
      const curXs = clusters[ci].groupIndices.map(gi => groupX[gi]);
      const left = Math.max(...prevXs) + 42;
      const right = Math.min(...curXs) - 42;
      if (right - left < 40) continue;
      const prevDate = effectiveDate(groups[clusters[ci - 1].groupIndices[0]][0]);
      const curDate = effectiveDate(groups[clusters[ci].groupIndices[0]][0]);
      const days = Math.round((new Date(curDate).getTime() - new Date(prevDate).getTime()) / MS_PER_DAY);
      labels.push({ x: (left + right) / 2, label: formatGap(days) });
    }
    return labels;
  }, [clusters, groupX, groups]);

  // interpolateX clamps to the first/last stick's x, so "today" still shows
  // pinned at an edge even if the whole history is past or future.
  const todayX = filtered.length ? interpolateX(Date.now(), times, groupX) : null;

  if (loading) {
    return <div style={{ padding: '20px 0', display: 'flex', justifyContent: 'center' }}><Spinner size={24} /></div>;
  }
  if (!entries || entries.length === 0) {
    return <div style={{ fontSize: 12, color: C.silver, padding: '8px 0' }}>{t('assets.no_versions')}</div>;
  }

  return (
    <div>
      <Legend legendFilter={legendFilter} onToggle={toggleLegendFilter} />

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 32 }}>
        {assetOptions.length > 1 && (
          <div style={{ minWidth: 200, maxWidth: 280 }}>
            <SearchableSelect
              value={assetFilter} onChange={setAssetFilter}
              options={assetOptions} placeholder="All assets"
            />
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <input
            type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            max={dateTo || undefined}
            style={{ padding: '5px 8px', borderRadius: 4, border: `1px solid ${C.mercury}`, fontSize: 12, fontFamily: 'Roboto, sans-serif' }}
          />
          <span style={{ fontSize: 11, color: C.silver }}>to</span>
          <input
            type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            min={dateFrom || undefined}
            style={{ padding: '5px 8px', borderRadius: 4, border: `1px solid ${C.mercury}`, fontSize: 12, fontFamily: 'Roboto, sans-serif' }}
          />
          {(dateFrom || dateTo) && (
            <button type="button" onClick={() => { setDateFrom(''); setDateTo(''); }} style={{
              border: 'none', background: 'none', cursor: 'pointer', fontSize: 11, color: C.liquid, padding: 0,
            }}>
              Clear
            </button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ fontSize: 12, color: C.silver, padding: '8px 0' }}>
          {assetFilter || dateFrom || dateTo ? 'No updates match your filters.' : t('assets.no_versions')}
        </div>
      ) : (
        <>
          {/* Width-matched to the chart below, not the card. */}
          <div ref={scrollRef} style={{ overflowX: 'auto', paddingBottom: 4 }}>
            <div style={{ width }}>
              <DateAxis minTime={minTime} maxTime={maxTime} times={times} groupX={groupX} />
              <div style={{ position: 'relative', width, height: STICK_AREA_HEIGHT + LABEL_HEIGHT + 1 }}>
                <div style={{ position: 'absolute', left: 0, right: 0, bottom: LABEL_HEIGHT, borderTop: `1px solid ${C.mercury}` }} />
                {todayX !== null && <TodayMarker x={todayX} />}
                {gapLabels.map((g, i) => <GapLabel key={i} x={g.x} label={g.label} />)}
              {clusters.map((cluster, ci) => {
                const { groupIndices } = cluster;
                const showDate = groupIndices.length === 1;
                const xs = groupIndices.map(gi => groupX[gi]);
                return (
                  <div key={ci}>
                    {groupIndices.map(gi => {
                      const group = groups[gi];
                      const baseX = groupX[gi];
                      const n = group.length;
                      if (n > 1) {
                        const groupKey = `grp:${group.map(e => e.id).join(',')}`;
                        return (
                          <GroupedStick
                            key={groupKey} group={group} x={baseX} showDate={showDate}
                            selected={selectedId === groupKey}
                            dimmed={selectedId !== null ? selectedId !== groupKey : !groupMatchesLegendFilter(group, legendFilter)}
                            onSelect={() => selectStick(groupKey)}
                          />
                        );
                      }
                      const entry = group[0];
                      return (
                        <Stick
                          key={entry.id} entry={entry} x={baseX} showDate={showDate}
                          selected={selectedId === entry.id}
                          dimmed={selectedId !== null ? selectedId !== entry.id : !matchesLegendFilter(entry, legendFilter)}
                          onSelect={selectStick}
                        />
                      );
                    })}
                    {!showDate && <ClusterDateBadge left={Math.min(...xs)} right={Math.max(...xs)} date={effectiveDate(groups[groupIndices[0]][0])} />}
                  </div>
                );
              })}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
